import React, { useCallback, useState } from 'react';
import { Form, Button, message, Spin, Card } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useMutation, useApolloClient, useQuery } from '@apollo/client';
// Api
import { SIGN_UP_BASIC } from 'api/auth/mutations';
import { SWITCH_TO_CREATOR } from 'api/auth/mutations';
import { CREATE_PRESIGNED_AVATAR_URL } from 'api/upload/mutations';
import {
  CHECK_EMAIL_AVAILABILITY,
  CHECK_USERNAME_AVAILABILITY,
} from 'api/auth/queries';
import { GET_SPORTS } from 'api/sports/queries';
import { EDIT_STORE_ADMIN } from 'api/store/mutations';
// Types
import {
  EditStoreAdmin,
  EditStoreAdminVariables,
} from 'api/store/types/EditStoreAdmin';
import { SignUpBasic, SignUpBasicVariables } from 'api/auth/types/SignUpBasic';
import {
  SwitchToCreator,
  SwitchToCreatorVariables,
} from 'api/auth/types/SwitchToCreator';
import {
  CreatePresignedAvatarUrl,
  CreatePresignedAvatarUrlVariables,
} from 'api/upload/types/CreatePresignedAvatarUrl';
import {
  CheckUsernameAvailability,
  CheckUsernameAvailabilityVariables,
} from 'api/auth/types/CheckUsernameAvailability';
import { Sports, SportsVariables } from 'api/sports/types/Sports';
import {
  AccountSubType,
  EarthCoordinateInput,
  SocialMediaType,
  StoreAthleticLevel,
  StoreGender,
} from 'api/graphql-global-types';
// Components
import OnboardingDataForm from './OnboardingDataForm';
import BasicDataForm from './BasicDataForm';
import CreatorSignupLink from './CreatorSignupLink';
// Helpers
import { uploadFilesToS3 } from 'helpers/single-uploader';
import { createLocation } from 'helpers/place';
import { getProperErrorMessage } from 'helpers/errors';
import { getSocialAccountError } from './helpers';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './AdminCreateUserForm.module.scss';

export type SocialAccount = {
  platform: SocialMediaType | undefined;
  link: string;
  isAdded: boolean;
  linkError?: string;
};

export type OnboardingFormData = {
  avatar?: File | string;
  profileName?: string;
  username?: string;
  accountType?: { value: AccountSubType; label: string };
  experienceLevel?: StoreAthleticLevel;
  gender?: StoreGender;
  sport?: {
    id: number;
    name: string;
  };
  bio?: string;
  phoneNumber?: string;
  phoneOptIn?: boolean;
  location?: {
    city: string | null;
    state?: string | null;
    stateCode?: string | null;
    country: string | null;
    countryCode: string | null;
    coordinates: EarthCoordinateInput | null;
  };
  socialAccounts?: SocialAccount[];
  email?: string;
  // Location fields
  country?: string;
  city?: string;
  state?: string;
  latitude?: number | null;
  longitude?: number | null;
  citySearch?: string;
};

export type SignUpData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  phoneOptIn: boolean;
  username?: string;
  avatar?: File | null;
};

type CombinedFormData = SignUpData & Partial<OnboardingFormData>;

const AdminCreateUserForm = (): JSX.Element => {
  const [form] = useForm();
  const client = useApolloClient();

  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([
    { platform: undefined, link: '', isAdded: false },
  ]);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [usernameChecking, setUsernameChecking] = useState(false);

  const { data: sportsData, loading: loadingSports } = useQuery<
    Sports,
    SportsVariables
  >(GET_SPORTS, {
    fetchPolicy: 'cache-first',
  });

  const [signupBasic, { loading: loadingSignup }] = useMutation<
    SignUpBasic,
    SignUpBasicVariables
  >(SIGN_UP_BASIC);

  const [switchToCreator, { loading: loadingSwitchToCreator }] = useMutation<
    SwitchToCreator,
    SwitchToCreatorVariables
  >(SWITCH_TO_CREATOR);

  const [editStoreAdmin] = useMutation<EditStoreAdmin, EditStoreAdminVariables>(
    EDIT_STORE_ADMIN
  );

  const [createPresignedAvatarUrl, { loading: loadingCreatePresignedUrl }] =
    useMutation<CreatePresignedAvatarUrl, CreatePresignedAvatarUrlVariables>(
      CREATE_PRESIGNED_AVATAR_URL
    );

  const loading =
    loadingSignup || loadingSwitchToCreator || loadingCreatePresignedUrl;

  const checkEmailAvailability = useCallback(
    async (email: string) => {
      try {
        const { data } = await client.query({
          query: CHECK_EMAIL_AVAILABILITY,
          variables: { input: { email } },
          fetchPolicy: 'network-only',
        });
        return data?.checkEmailAvailability?.isAvailable ?? false;
      } catch (error) {
        return false;
      }
    },
    [client]
  );

  const checkUsernameAvailability = useCallback(
    async (username: string) => {
      try {
        setUsernameChecking(true);
        const { data } = await client.query<
          CheckUsernameAvailability,
          CheckUsernameAvailabilityVariables
        >({
          query: CHECK_USERNAME_AVAILABILITY,
          variables: { input: { username } },
          fetchPolicy: 'network-only',
        });
        return data?.checkUsernameAvailability?.isAvailable ?? false;
      } catch (error: any) {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const graphQLError = error.graphQLErrors[0];
          if (graphQLError.extensions?.code === 'BAD_USER_INPUT') {
            const validationMessage =
              graphQLError.extensions?.response?.message?.[0];
            throw new Error(`VALIDATION_ERROR: ${validationMessage}`);
          }
        }
        return false;
      } finally {
        setUsernameChecking(false);
      }
    },
    [client]
  );

  const handleCreateUser = useCallback(
    async (values: CombinedFormData) => {
      try {
        const socialAccountErrors = socialAccounts.map(getSocialAccountError);
        const socialAccountError = socialAccountErrors.find(Boolean);
        if (socialAccountError) {
          setSocialAccounts((prev) =>
            prev.map((account, index) => ({
              ...account,
              linkError: socialAccountErrors[index],
            }))
          );
          message.error(socialAccountError);
          return;
        }

        const isEmailAvailable = await checkEmailAvailability(values.email);
        if (!isEmailAvailable) {
          message.error('Email is already taken');
          return;
        }

        if (values.username) {
          try {
            const isUsernameAvailable = await checkUsernameAvailability(
              values.username
            );
            if (!isUsernameAvailable) {
              message.error('Username is already taken');
              return;
            }
          } catch (error: any) {
            if (error.message.startsWith('VALIDATION_ERROR:')) {
              message.error(
                'Username can only contain lowercase letters, numbers, hyphens, underscores, and periods'
              );
            } else {
              message.error('Failed to check username availability');
            }
            return;
          }
        }

        const { data: signupData } = await signupBasic({
          variables: {
            input: {
              basic: {
                firstName: values?.firstName || '',
                lastName: values?.lastName || '',
                email: values?.email || '',
                password: values.password || '',
                ...(values?.phoneNumber && {
                  phoneNumberE164: values?.phoneNumber,
                }),
                phoneOptIn: values?.phoneOptIn ?? false,
              },
            },
          },
        });

        if (signupData?.signUpBasic.tokens && signupData?.signUpBasic.userId) {
          let avatarKey = '';
          if (avatarFile) {
            const filenameParts = avatarFile.name.split('.');
            const { data: presignedUrlsData } = await createPresignedAvatarUrl({
              variables: {
                input: {
                  ext: filenameParts[filenameParts.length - 1],
                  contentType: avatarFile.type,
                },
              },
            });

            if (presignedUrlsData?.createPresignedAvatarUrl) {
              await uploadFilesToS3(
                [
                  {
                    ...presignedUrlsData.createPresignedAvatarUrl,
                    fileWithMeta: {
                      contentType: avatarFile.type,
                      file: avatarFile,
                      ext: filenameParts[filenameParts.length - 1],
                    },
                  },
                ],
                {
                  isStrict: true,
                  withoutContentType: false,
                }
              );
              avatarKey = presignedUrlsData.createPresignedAvatarUrl.key;
            }
          }

          const validSocialAccounts = socialAccounts
            .filter(
              (account) => account.platform && account.link && account.isAdded
            )
            .map((account) => ({
              platform: account.platform as SocialMediaType,
              link: account.link,
            }));

          const isAthlete =
            values.accountType?.value === AccountSubType.Athlete;

          await switchToCreator({
            variables: {
              input: {
                ownerId: signupData?.signUpBasic.userId,
                username:
                  values.username ||
                  `${values.firstName}_${values.lastName}`.toLowerCase(),
                profileName:
                  values.profileName ||
                  `${values.firstName} ${values.lastName}`,
                subtype: values.accountType?.value || AccountSubType.Athlete,
                sportIds: values.sport ? [Number(values.sport)] : [],
                phoneNumberE164: values?.phoneNumber || '',
                phoneOptIn: values?.phoneOptIn ?? false,
                ...(avatarKey && { avatarKey }),
                ...(values.bio && { bio: values.bio }),
                ...(isAthlete &&
                  values.experienceLevel && {
                    level: values.experienceLevel,
                  }),
                ...(isAthlete && values.gender && { gender: values.gender }),
                // Location data
                ...(values.country && { country: values.country }),
                ...(values.city && { city: values.city }),
                ...(values.state && { state: values.state }),
                ...(createLocation(values.latitude, values.longitude) && {
                  location: createLocation(values.latitude, values.longitude),
                }),
                // Social accounts
                ...(validSocialAccounts.length > 0 && {
                  socialAccounts: validSocialAccounts,
                }),
              },
            },
          });
          await editStoreAdmin({
            variables: {
              input: {
                storeId: signupData?.signUpBasic.userId,
                userDetails: {
                  email: values?.email || '',
                },
                storeDetails: {},
              },
            },
          });

          form.resetFields();
          setSocialAccounts([
            { platform: undefined, link: '', isAdded: false },
          ]);
          setAvatarFile(null);
          successNotification(
            'User created and converted to creator successfully!'
          );
        }
      } catch (error: any) {
        console.error(error);
        errorNotification(
          getProperErrorMessage(error, error?.message || 'Something went wrong')
        );
      }
    },
    [
      signupBasic,
      switchToCreator,
      createPresignedAvatarUrl,
      checkEmailAvailability,
      checkUsernameAvailability,
      editStoreAdmin,
      form,
      socialAccounts,
      avatarFile,
    ]
  );

  const handleFormSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);

      const formData = {
        ...values,
        phoneOptIn: Boolean(values.phoneOptIn),
        socialAccounts,
      };

      await handleCreateUser(formData);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  return (
    <Card
      title="Create User Profile"
      extra={<CreatorSignupLink />}
      className={styles.createUserCard}
    >
      <div className={styles.loadingContainer}>
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleFormSubmit}
            className={styles.formContainer}
            initialValues={{
              firstName: '',
              lastName: '',
              email: '',
              password: '',
              phoneNumber: '',
              phoneOptIn: false,
              username: '',
              profileName: '',
              bio: '',
              socialAccounts: [
                { platform: undefined, link: '', isAdded: false },
              ],
              country: '',
              city: '',
              state: '',
              latitude: null,
              longitude: null,
              citySearch: '',
              avatarValidation: null,
              socialAccountsValidation: null,
            }}
          >
            {/* Basic Data Form Component */}
            <BasicDataForm form={form} usernameChecking={usernameChecking} />

            {/* Onboarding Data Form Component */}
            <OnboardingDataForm
              form={form}
              sportsData={sportsData}
              loadingSports={loadingSports}
              avatarFile={avatarFile}
              setAvatarFile={setAvatarFile}
              socialAccounts={socialAccounts}
              setSocialAccounts={setSocialAccounts}
            />

            {/* Submit Button */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                className={styles.submitButton}
                block
              >
                Create User Profile
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Card>
  );
};

export default AdminCreateUserForm;
